import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import './AdminModals.css';
import CountryCodePicker from '../CountryCodePicker';
import TimezoneSwitcher from '../TimezoneSwitcher';
import { timezones } from '../../data/timezones';
const API_URL = process.env.REACT_APP_API_URL;

const EditUserModal = ({ isOpen, onClose, onUserUpdated, userId }) => {
  const { t } = useTranslation();
  const { token, user: adminUser } = useAuth();
  
  const [formData, setFormData] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isTimezoneOpen, setIsTimezoneOpen] = useState(false);
  const [availableLanguages, setAvailableLanguages] = useState([]);

  useEffect(() => {
    const fetchInitialData = async () => {
        if (isOpen && userId) {
            setIsLoading(true);
            setError('');
            setNewPassword('');
            try {
                const [userResponse, langsResponse] = await Promise.all([
                    fetch(`${API_URL}/api/admin/users/${userId}`, { headers: { 'Authorization': `Bearer ${token}` } }),
                    fetch(`${API_URL}/api/public/languages`)
                ]);
                
                if (!userResponse.ok) throw new Error(t('error_fetch_user_data'));
                const userData = await userResponse.json();
                
                if (langsResponse.ok) {
                    const langsData = await langsResponse.json();
                    setAvailableLanguages(langsData);
                }
				
				let parsedExpertises = [];
                if (userData.expertises && typeof userData.expertises === 'string') {
                    try { parsedExpertises = JSON.parse(userData.expertises); } catch (e) { console.error("Expertises JSON parse hatası:", e); parsedExpertises = []; }
                } else if (Array.isArray(userData.expertises)) {
                    parsedExpertises = userData.expertises;
                }

                setFormData({
                    firstName: userData.first_name || '',
                    lastName: userData.last_name || '',
                    email: userData.email || '',
                    role: userData.role || 'customer',
                    status: userData.status || 'active',
					translator_status: userData.translatorProfile?.status || 'pending',
                    phone_country_code: userData.phone_country_code || '+90',
                    phone_number: userData.phone_number || '',
                    mainLanguage: userData.main_language || '',
                    country: userData.country || '',
                    city: userData.city || '',
                    timezone: userData.timezone || 'Europe/Istanbul',
                    expertises: parsedExpertises,
					email_notifications_enabled: userData.email_notifications_enabled, // 1 veya 0 olarak gelecek
                    push_notifications_enabled: userData.push_notifications_enabled, 
                    languagePairs: userData.languagePairs && userData.languagePairs.length > 0 ? userData.languagePairs : [{ source: 'tr', target: 'en' }]
                });
            } catch (err) {
                setError(err.message);
            } finally {
                setIsLoading(false);
            }
        }
    };
    fetchInitialData();
  }, [isOpen, userId, token, t]);
 const handleNotificationToggle = (key) => {
    setFormData(p => ({ ...p, [key]: p[key] === 1 ? 0 : 1 }));
  };
  const handleChange = (e) => setFormData(p => ({ ...p, [e.target.name]: e.target.value }));
  const handleCountryCodeChange = (code) => setFormData(p => ({ ...p, phone_country_code: code }));
  const handleTimezoneChange = (tz) => setFormData(p => ({ ...p, timezone: tz }));
  const handleExpertiseChange = (expertise) => {
    setFormData(p => {
        const currentExpertises = p.expertises || [];
        const newExpertises = currentExpertises.includes(expertise)
          ? currentExpertises.filter(e => e !== expertise)
          : [...currentExpertises, expertise];
        return { ...p, expertises: newExpertises };
    });
  };
  const handleAddPair = () => setFormData(p => ({ ...p, languagePairs: [...p.languagePairs, { source: 'tr', target: 'en' }] }));
  const handleRemovePair = (index) => setFormData(p => ({ ...p, languagePairs: p.languagePairs.filter((_, i) => i !== index) }));
  const handlePairChange = (index, field, value) => {
    const newPairs = [...formData.languagePairs];
    newPairs[index][field] = value;
    setFormData(p => ({ ...p, languagePairs: newPairs }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
   const submissionData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        role: formData.role,
        status: formData.status,
        countryCode: formData.phone_country_code,
        phone: formData.phone_number,
        newPassword: newPassword || null,
        mainLanguage: formData.mainLanguage,
        country: formData.country,
        city: formData.city,
        timezone: formData.timezone,
        expertises: formData.expertises,
        languagePairs: formData.languagePairs,
		email_notifications_enabled: formData.email_notifications_enabled,
        push_notifications_enabled: formData.push_notifications_enabled,
		translatorStatus: formData.translator_status
    };
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(submissionData)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(t(data.messageKey) || t('error_user_update'));
      onUserUpdated();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const isSuperAdmin = adminUser.id === 1;
  const isEditingSelf = adminUser.id === userId;
  const isEditingAnotherAdmin = formData?.role === 'admin';
  const ALL_EXPERTISES = ['general', 'legal', 'medical', 'technical', 'academic'];

  return ReactDOM.createPortal(
    <>
      <div className="admin-modal-overlay" onClick={onClose}>
        <div className="admin-modal-content large" onClick={(e) => e.stopPropagation()}>
          <div className="admin-modal-header">
            <h3>{t('edit_user')}</h3>
            <button onClick={onClose} className="close-btn">×</button>
          </div>
          {isLoading || !formData ? (
            <div className="modal-loader">
              <i className="fas fa-spinner fa-spin"></i> {t('loading_user_data')}
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="admin-modal-form">
              <div className="form-row-auth">
                <div className="form-group-auth floating-label half-width"><input id="edit-firstName" name="firstName" type="text" value={formData.firstName || ''} onChange={handleChange} required placeholder=" " /><label htmlFor="edit-firstName">{t('first_name')}</label></div>
                <div className="form-group-auth floating-label half-width"><input id="edit-lastName" name="lastName" type="text" value={formData.lastName || ''} onChange={handleChange} required placeholder=" " /><label htmlFor="edit-lastName">{t('last_name')}</label></div>
              </div>
              <div className="form-group-auth floating-label">
                <input id="edit-email" name="email" type="email" value={formData.email || ''} onChange={handleChange} required placeholder=" " /><label htmlFor="edit-email">{t('email_address')}</label>
              </div>
              <div className="form-row-auth">
                <div className="form-group-auth phone-code">
                    <CountryCodePicker selectedCode={formData.phone_country_code || '+90'} onCodeChange={handleCountryCodeChange} />
                </div>
                <div className="form-group-auth floating-label phone-number">
                    <input name="phone_number" type="tel" value={formData.phone_number || ''} onChange={handleChange} placeholder=" " />
                    <label>{t('phone')}</label>
                </div>
              </div>
              <div className="form-group-auth floating-label">
                <input id="edit-newPassword" name="newPassword" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder=" " />
                <label htmlFor="edit-newPassword">{t('reset_password_optional')}</label>
              </div>
              <div className="form-row-auth">
                <div className="form-group-auth half-width">
                  <label className="static-label">{t('role')}</label>
                  <select name="role" value={formData.role} onChange={handleChange} disabled={!isSuperAdmin && (isEditingAnotherAdmin || isEditingSelf)}>
                      <option value="customer">{t('role_customer')}</option>
                      <option value="translator">{t('role_translator')}</option>
                      <option value="support">{t('role_support')}</option>
                      {isSuperAdmin && <option value="admin">{t('role_admin')}</option>}
                  </select>
                </div>
                <div className="form-group-auth half-width">
                  <label className="static-label">{t('status')}</label>
                  <select name="status" value={formData.status} onChange={handleChange} disabled={isEditingSelf}>
                      <option value="active">{t('status_active')}</option>
                      <option value="disabled">{t('status_disabled')}</option>
                  </select>
                </div>
              </div>
			  
{['customer', 'support', 'admin'].includes(formData.role) && (
                  <div className="form-group-auth">
                      <label className="static-label">{t('local_timezone')}</label>
                      <div className="timezone-display-input" onClick={() => setIsTimezoneOpen(true)}>
                          {formData.timezone || t('select_timezone')}
                      </div>
                  </div>
              )}
              {formData.role === 'translator' && (
                  <>
				   <div className="form-group-auth">
                          <label className="static-label">{t('translator_profile_status', 'Çevirmen Profili Durumu')}</label>
                          <select 
                              name="translator_status" 
                              value={formData.translator_status} 
                              onChange={handleChange}
                          >
                              <option value="pending">{t('status_pending_approval')}</option>
                              <option value="approved">{t('status_approved')}</option>
                              <option value="rejected">{t('status_rejected')}</option>
                              <option value="suspended">{t('status_suspended')}</option>
                          </select>
                      </div>
					  <hr className="modal-form-divider" />
				  <div className="form-group-auth">      
				  <label className="static-label">{t('notification_settings')}</label></div>

                                    <div className="setting-row modal-setting">
                                        <div className="setting-info">
                                            <strong>{t('email_notifications')}</strong>
                                       
                                        </div>
                                        <label className="switch">
                                            <input 
                                                type="checkbox" 
                                                checked={formData.email_notifications_enabled === 1} 
                                                onChange={() => handleNotificationToggle('email_notifications_enabled')} 
                                            />
                                            <span className="slider round"></span>
                                        </label>
                                    </div>
                                    <div className="setting-row modal-setting">
                                        <div className="setting-info">
                                            <strong>{t('browser_notifications')}</strong>
                     
                                        </div>
                                        <label className="switch">
                                            <input 
                                                type="checkbox" 
                                                checked={formData.push_notifications_enabled === 1} 
                                                onChange={() => handleNotificationToggle('push_notifications_enabled')} 
                                            />
                                            <span className="slider round"></span>
                                        </label>
                                    </div>
                        
                      <hr className="modal-form-divider" />
                      <div className="form-group-auth floating-label">
                          <input id="admin-mainLanguage" name="mainLanguage" type="text" value={formData.mainLanguage} onChange={handleChange} placeholder=" " />
                          <label htmlFor="admin-mainLanguage">{t('main_language')}</label>
                      </div>
                      <div className="form-row-auth">
                          <div className="form-group-auth floating-label half-width">
                              <input id="admin-country" name="country" type="text" value={formData.country} onChange={handleChange} placeholder=" " />
                              <label htmlFor="admin-country">{t('country')}</label>
                          </div>
                          <div className="form-group-auth floating-label half-width">
                              <input id="admin-city" name="city" type="text" value={formData.city} onChange={handleChange} placeholder=" " />
                              <label htmlFor="admin-city">{t('city')}</label>
                          </div>
                      </div>
                      <div className="form-group-auth">
                          <label className="static-label">{t('local_timezone')}</label>
                          <div className="timezone-display-input" onClick={() => setIsTimezoneOpen(true)}>{formData.timezone}</div>
                      </div>
                      <div className="form-group-auth">
                          <label className="static-label">{t('expertise_area')}</label>
                          <div className="checkbox-grid-modal">{ALL_EXPERTISES.map(exp => ( <label key={exp} className="chip-checkbox-modal"><input type="checkbox" checked={formData.expertises.includes(exp)} onChange={() => handleExpertiseChange(exp)} /><span>{t(exp)}</span></label>))}</div>
                      </div>
                      <div className="form-group-auth">
                          <label className="static-label">{t('language_pairs')}</label>
                          {formData.languagePairs.map((pair, index) => (
                              <div className="lang-pair-row-modal" key={index}>
                                  <select value={pair.source} onChange={(e) => handlePairChange(index, 'source', e.target.value)}>{availableLanguages.map(lang => <option key={lang.code} value={lang.code}>{lang.name}</option>)}</select>
                                  <span>&rarr;</span>
                                  <select value={pair.target} onChange={(e) => handlePairChange(index, 'target', e.target.value)}>{availableLanguages.map(lang => <option key={lang.code} value={lang.code}>{lang.name}</option>)}</select>
                                  <button type="button" onClick={() => handleRemovePair(index)} className="remove-pair-btn">&times;</button>
                              </div>
                          ))}
                          <button type="button" onClick={handleAddPair} className="add-pair-btn-modal">+ {t('add_pair')}</button>
                      </div>
            
             
                  </>
              )}
              {error && <p className="auth-error">{error}</p>}
              <div className="admin-modal-footer">
                <button type="button" onClick={onClose} className="btn-secondary">{t('cancel')}</button>
                <button type="submit" className="cta-button" disabled={isLoading}>{isLoading ? t('saving') : t('save_changes')}</button>
              </div>
            </form>
          )}
        </div>
      </div>
      <TimezoneSwitcher isOpen={isTimezoneOpen} onClose={() => setIsTimezoneOpen(false)} timezones={timezones} selectedTimezone={formData?.timezone} onTimezoneChange={handleTimezoneChange}/>
    </>,
    document.getElementById('modal-root')
  );
};

export default EditUserModal;